import {
  IAgencyCostActivityImperial,
  IAgencyCostActivityMetric,
  IAgencyCostImperial,
  IAgencyCostLineItemImperial,
  IAgencyCostLineItemMetric,
  IAgencyCostMetric,
} from '@/modules/agencyCost/interfaces/agencyCost';
import { UnitSystem } from '@/interfaces/units';
import convert from 'convert-units';
import {
  ImperialUnits,
  KGM3_TO_LBFT3,
  LBFT3_TO_KGM3,
  MetricUnits,
  roundAfterUnitConversion,
  UnitMapImperialToMetric,
  UnitMapMetricToImperial,
} from '@/lib/unitConversion/unitConversionFactors';
import { convertImperialToMetric, convertMetricToImperial } from '@/modules/lcca/lib/unitConversion/lccaScenario';


export const agencyCostImperialToMetric = (A: IAgencyCostImperial): IAgencyCostMetric => {
  const B: IAgencyCostMetric = {
    // Base Scenario
    name: A.name,
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: A.displayUnits,
    scenarioType: A.scenarioType,
    isComputedFromLegacy: A.isComputedFromLegacy,

    unitSystem: UnitSystem.Metric,
    isDefault: A.isDefault,

    district: A.district,
    county: A.county,
    route: A.route,

    fromLocation: A.fromLocation,
    toLocation: A.toLocation,

    numTravelLanes: A.numTravelLanes,
    numDirections: A.numDirections,

    startYear: A.startYear,
    discountRate_pct: A.discountRate_pct,

    // Scenario dimensioned variables
    fromStation_m: convert(A.fromStation_ft || 0).from('ft').to('m'),
    toStation_m: convert(A.toStation_ft || 0).from('ft').to('m'),

    totalLength_km: convert(A.totalLength_mi).from('mi').to('km'),

    laneWidth_m: convert(A.laneWidth_ft).from('ft').to('m'),
    insideShoulderWidth_m: convert(A.insideShoulderWidth_ft).from('ft').to('m'),
    outsideShoulderWidth_m: convert(A.outsideShoulderWidth_ft).from('ft').to('m'),
    jointSpacing_m: convert(A.jointSpacing_ft || 0).from('ft').to('m'),

    activities: A.activities.map((activity) => agencyActivityImperialToMetric(activity)),
  };

  return B;
};

export const agencyCostMetricToImperial = (A: IAgencyCostMetric): IAgencyCostImperial => {
  const B: IAgencyCostImperial = {
    // Base Scenario
    name: A.name,
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: A.displayUnits,
    scenarioType: A.scenarioType,
    isComputedFromLegacy: A.isComputedFromLegacy,

    unitSystem: UnitSystem.Imperial,
    isDefault: A.isDefault,

    district: A.district,
    county: A.county,
    route: A.route,

    fromLocation: A.fromLocation,
    toLocation: A.toLocation,

    numTravelLanes: A.numTravelLanes,
    numDirections: A.numDirections,

    startYear: A.startYear,
    discountRate_pct: A.discountRate_pct,

    // Scenario dimensioned variables
    fromStation_ft: convert(A.fromStation_m || 0).from('m').to('ft'),
    toStation_ft: convert(A.toStation_m || 0).from('m').to('ft'),

    totalLength_mi: convert(A.totalLength_km).from('km').to('mi'),

    laneWidth_ft: convert(A.laneWidth_m).from('m').to('ft'),
    insideShoulderWidth_ft: convert(A.insideShoulderWidth_m).from('m').to('ft'),
    outsideShoulderWidth_ft: convert(A.outsideShoulderWidth_m).from('m').to('ft'),
    jointSpacing_ft: convert(A.jointSpacing_m || 0).from('m').to('ft'),

    activities: A.activities.map((activity) => agencyActivityMetricToImperial(activity)),
  };

  return B;
};

const agencyActivityImperialToMetric = (A: IAgencyCostActivityImperial): IAgencyCostActivityMetric => {
  const B: IAgencyCostActivityMetric = {
    unitSystem: UnitSystem.Metric,
    id: A.id,
    name: A.name,
    yearsAfterStart: A.yearsAfterStart,

    lineItems: A.lineItems.map((item) => agencyLineItemImperialToMetric(item)),
  };

  return B;
};

const agencyActivityMetricToImperial = (A: IAgencyCostActivityMetric): IAgencyCostActivityImperial => {
  const B: IAgencyCostActivityImperial = {
    unitSystem: UnitSystem.Imperial,
    id: A.id,
    name: A.name,
    yearsAfterStart: A.yearsAfterStart,

    lineItems: A.lineItems.map((item) => agencyLineItemMetricToImperial(item)),
  };

  return B;
};

export const agencyLineItemImperialToMetric = (A: IAgencyCostLineItemImperial): IAgencyCostLineItemMetric => {
  const B: IAgencyCostLineItemMetric = {
    id: A.id,
    unitSystem: UnitSystem.Metric,
    displayUnits: A.displayUnits,
    activity: A.activity,
    name: A.name,
    type: A.type,
    mainline: A.mainline,
    description: A.description,
    isDefault: A.isDefault, // Used to identify default values pulled from DB
    fromDefault: A.fromDefault, // Used to identify if generated from default value instead of custom

    units: (function () {
      if (A.units !== null && Object.keys(UnitMapImperialToMetric).includes(A.units)) {
        return UnitMapImperialToMetric[A.units];
      }
      return MetricUnits.UNITS;
    }()),
    unitCost: roundAfterUnitConversion((A.unitCost || 0) / convertImperialToMetric(A.units, 1), 2, true),
    quantity: convertImperialToMetric(A.units, (A.quantity || 0)),

    width_m: convert(A.width_ft || 0).from('ft').to('m'),
    thickness_cm: convert(A.thickness_in || 0).from('in').to('cm'),
    density_kgPerM3: A.density_lbPerFt3 ? A.density_lbPerFt3 * LBFT3_TO_KGM3 : A.density_lbPerFt3,
  };

  return B;
};

export const agencyLineItemMetricToImperial = (A: IAgencyCostLineItemMetric): IAgencyCostLineItemImperial => {
  const B: IAgencyCostLineItemImperial = {
    id: A.id,
    unitSystem: UnitSystem.Imperial,
    displayUnits: A.displayUnits,
    activity: A.activity,
    name: A.name,
    type: A.type,
    mainline: A.mainline,
    description: A.description,
    isDefault: A.isDefault, // Used to identify default values pulled from DB
    fromDefault: A.fromDefault, // Used to identify if generated from default value instead of custom

    units: (function () {
      if (A.units !== null && Object.keys(UnitMapMetricToImperial).includes(A.units)) {
        return UnitMapMetricToImperial[A.units];
      }
      return ImperialUnits.UNITS;
    }()),
    unitCost: roundAfterUnitConversion((A.unitCost || 0) / convertMetricToImperial(A.units, 1), 2, true),
    quantity: convertMetricToImperial(A.units, (A.quantity || 0)),

    width_ft: convert(A.width_m || 0).from('m').to('ft'),
    thickness_in: convert(A.thickness_cm || 0).from('cm').to('in'),
    density_lbPerFt3: A.density_kgPerM3 ? A.density_kgPerM3 * KGM3_TO_LBFT3 : A.density_kgPerM3,

  };

  return B;
};
